import type { DesignerRequestCountsResponse } from "@nlc/shared-types";

import { useQuery } from "@tanstack/react-query";

import { useUser } from "@/hooks/useUser";
import apiClient from "@/lib/api/client";

import { CACHE_CONFIG } from "../../constants/pagination";

/**
 * Options for configuring the Designer request counts query
 */
interface UseDesignerRequestCountsOptions {
  /** Whether to enable the query. Useful for conditional fetching. Defaults to true. */
  enabled?: boolean;
  /** Override user context: fetch counts for this user instead of the authenticated user. */
  userId?: string;
}

/**
 * Fetches request counts for Designer role
 *
 * @description
 * Retrieves aggregated counts for "Assigned to Me" and "Assigned to Any" tabs,
 * broken down by status sections (In Progress, Being Revised, Awaiting Reply,
 * Ready for Review, Others).
 *
 * The hook automatically:
 * - Refetches every 2 minutes when the browser tab is visible
 * - Caches data for 10 minutes to reduce server load
 * - Garbage collects unused data after 30 minutes
 * - Only fetches when user is authenticated and is a Designer
 *
 * @param {UseDesignerRequestCountsOptions} options - Configuration options
 * @param {boolean} [options.enabled=true] - Whether to enable the query
 *
 * @returns {Object} TanStack Query result object
 * @returns {DesignerRequestCountsResponse} result.data - Designer request counts grouped by tab and section
 * @returns {Object} result.data.counts - Main counts object
 * @returns {Object} result.data.counts.assignedToMe - Counts for requests assigned to current designer
 * @returns {number} result.data.counts.assignedToMe.inProgress - Requests in "Design in Progress" status
 * @returns {number} result.data.counts.assignedToMe.beingRevised - Requests in "Being Revised" status
 * @returns {number} result.data.counts.assignedToMe.awaitingReply - Requests awaiting customer response
 * @returns {number} result.data.counts.assignedToMe.readyForReview - Requests ready for review
 * @returns {number} result.data.counts.assignedToMe.others - Requests in other statuses (Processing, In Queue)
 * @returns {number} result.data.counts.assignedToMe.total - Total assigned to current designer
 * @returns {Object} result.data.counts.assignedToAny - Counts for all team requests (team projects + assigned requests)
 * @returns {number} result.data.counts.assignedToAny.inProgress - Team's in-progress requests
 * @returns {number} result.data.counts.assignedToAny.beingRevised - Team's revised requests
 * @returns {number} result.data.counts.assignedToAny.awaitingReply - Team's awaiting reply requests
 * @returns {number} result.data.counts.assignedToAny.readyForReview - Team's ready for review requests
 * @returns {number} result.data.counts.assignedToAny.others - Team's other status requests
 * @returns {number} result.data.counts.assignedToAny.total - Total team requests
 * @returns {boolean} result.isLoading - True while fetching initial data
 * @returns {boolean} result.isError - True if fetch failed
 * @returns {Error} result.error - Error object if fetch failed
 * @returns {boolean} result.isFetching - True while any fetch is in progress (including background refetch)
 *
 * @example
 * // Basic usage
 * const { data, isLoading } = useDesignerRequestCounts();
 * if (data) {
 *   console.log(`In Progress: ${data.counts.assignedToMe.inProgress}`);
 * }
 *
 * @example
 * // Conditional fetching (only when designer view is active)
 * const { data } = useDesignerRequestCounts({ enabled: isDesignerView });
 *
 * @see {@link useDesignerRequestData} - For fetching actual request data (not just counts)
 * @see {@link AssignedToMeTab} - Main consumer of this hook for "Assigned to Me" tab
 * @see {@link AssignedToAnyTab} - Main consumer of this hook for "Assigned to Any" tab
 */
export function useDesignerRequestCounts(
  options: UseDesignerRequestCountsOptions = {},
) {
  const { data: userData } = useUser();
  const { enabled = true, userId } = options;

  return useQuery({
    enabled: !!userData?.user && enabled,
    gcTime: CACHE_CONFIG.GC_TIME,
    queryFn: async () => {
      const url = userId
        ? `/requests/counts/designer?forUserId=${userId}`
        : "/requests/counts/designer";

      return await apiClient
        .url(url)
        .get()
        .json<DesignerRequestCountsResponse>();
    },
    queryKey: ["designerRequestCounts", userId ?? "self"],
    refetchInterval: (data) =>
      data && document.visibilityState === "visible"
        ? CACHE_CONFIG.REFETCH_INTERVAL
        : false,
    staleTime: CACHE_CONFIG.STALE_TIME,
  });
}
